/**
 * documentRepositoryDateRange.js — historia 170 (Repositorio Documental), J2. RN28/G1-8 (DG-10):
 * the "Upload date" filter picks whole CALENDAR DAYS in the browser's timezone; the server receives
 * INSTANTS, inclusive on both ends — so the "to" instant is midnight of the day AFTER the one chosen
 * (exclusive upper bound). Pure functions, no React import.
 *
 * "No date" is always `''` in business logic (CLAUDE.md: no `null`/`undefined`); the translation to
 * R2's nullable `createdFromMillis`/`createdToMillis` happens ONLY at `documentRepositoryApi.js`
 * (the API boundary), exactly like every other criteria field.
 */

const pad2 = (value) => String(value).padStart(2, '0')

// Mirrors `generic/DateColumnFilterPopover.jsx`'s own `RANGE_DELIMITER` (a pipe, not a comma — the
// pagination request travels as a query string and `URLSearchParams` joins array entries with
// commas, which would split a comma-delimited value across two slots).
const RANGE_DELIMITER = '|'

/** 'YYYY-MM-DD' -> local midnight of that day, in millis. '' (blank) -> ''. */
export const dateStringToLocalMidnightMillis = (dateString) => {
  if (!dateString) return ''
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day, 0, 0, 0, 0).getTime()
}

/** 'YYYY-MM-DD' -> local midnight of the DAY AFTER (exclusive upper bound, RN28). '' -> ''. */
export const dateStringToExclusiveEndMillis = (dateString) => {
  const startOfDay = dateStringToLocalMidnightMillis(dateString)
  if (startOfDay === '') return ''
  const nextDay = new Date(startOfDay)
  nextDay.setDate(nextDay.getDate() + 1)
  return nextDay.getTime()
}

/** Millis (local midnight) -> 'YYYY-MM-DD', for display. '' -> ''. */
export const localMidnightMillisToDateString = (millis) => {
  if (millis === '') return ''
  const date = new Date(millis)
  return date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate())
}

/** Inverse of `dateStringToExclusiveEndMillis`: subtracts one day back before formatting. '' -> ''. */
export const exclusiveEndMillisToDateString = (millis) => {
  if (millis === '') return ''
  const date = new Date(millis)
  date.setDate(date.getDate() - 1)
  return localMidnightMillisToDateString(date.getTime())
}

/**
 * V5 — a range is invalid only when BOTH sides are present and "from" is strictly after "to"
 * (S51). Either side missing is always valid (V5: "cualquiera puede faltar"); equal single-day
 * ranges are valid (RN28: inclusive on both ends) because `toExclusiveMillis` is already the day
 * AFTER, so `fromMillis < toExclusiveMillis` holds even when both name the same calendar day.
 */
export const isValidDateRange = (fromMillis, toExclusiveMillis) =>
  fromMillis === '' || toExclusiveMillis === '' || fromMillis < toExclusiveMillis

// ---------------------------------------------------------------------------------------------
// Refactor (column filters) — `generic/DateColumnFilterPopover.jsx` is the "Upload date" column's
// own filter widget: it stores an ISO 8601 INSTANT (`toISOString()`, always UTC) plus an operator
// (`gt`/`gte`/`lt`/`lte`/`between`, serialized `iso1|iso2` for `between`). RN28 only cares about the
// LOCAL CALENDAR DATE the user picked, never the time-of-day the popover's `DateTimePicker` also
// happens to offer — so only the date portion (read in LOCAL time, never `toISOString`'s own UTC
// string) ever reaches `dateStringToLocalMidnightMillis`/`dateStringToExclusiveEndMillis` above.
// Sub-day granularity collapses by direction only: `gt`/`gte` both mean "from that day" (inclusive
// lower bound), `lt`/`lte` both mean "up to and including that day" (exclusive upper bound, RN28).

/** ISO 8601 instant -> 'YYYY-MM-DD' in LOCAL time (never the UTC date `toISOString` itself carries). */
export const isoInstantToLocalDateString = (iso) => {
  if (!iso) return ''
  const date = new Date(iso)
  if (isNaN(date.getTime())) return ''
  return date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate())
}

/** Local midnight of 'YYYY-MM-DD' -> ISO 8601 instant, for seeding the popover's own stored value. */
const localDateStringToIsoInstant = (dateString) => {
  const millis = dateStringToLocalMidnightMillis(dateString)
  return millis === '' ? '' : new Date(millis).toISOString()
}

/**
 * `DateColumnFilterPopover`'s stored `(value, operator)` -> RN28 full-day millis
 * (`{createdFromMillis, createdToMillis}`, criteria shape — `''` sentinel, never `null`).
 *
 * V5 (S51) — unlike the old `<input type='date'>` pair (whose `onChange` simply refused to touch
 * `criteria` on an invalid range, so no request went out at all), the generic popover's own "Apply"
 * has no cross-field validation and WILL fire a request the moment both dates are filled in
 * (`generic/DateColumnFilterPopover.jsx`'s `canApply` only checks both are present) — gating that
 * button itself would be an invasive change to a shared component. The closest equivalent short of
 * that: an invalid `between` range (From after To) resolves to NO filter at all (both blank) rather
 * than sending the nonsensical range to Sun — the request still fires, but without that criterion,
 * instead of being blocked outright.
 */
export const createdDateRangeFromColumnFilter = (value, operator) => {
  if (!value) return { createdFromMillis: '', createdToMillis: '' }
  if (operator === 'between') {
    const [fromIso, toIso] = String(value).split(RANGE_DELIMITER)
    const createdFromMillis = dateStringToLocalMidnightMillis(isoInstantToLocalDateString(fromIso))
    const createdToMillis = dateStringToExclusiveEndMillis(isoInstantToLocalDateString(toIso))
    if (!isValidDateRange(createdFromMillis, createdToMillis)) return { createdFromMillis: '', createdToMillis: '' }
    return { createdFromMillis, createdToMillis }
  }
  const dateString = isoInstantToLocalDateString(value)
  if (operator === 'lt' || operator === 'lte') {
    return { createdFromMillis: '', createdToMillis: dateStringToExclusiveEndMillis(dateString) }
  }
  // 'gt' / 'gte' (default): "from that day" onwards.
  return { createdFromMillis: dateStringToLocalMidnightMillis(dateString), createdToMillis: '' }
}

/** Inverse of the above: RN28 full-day millis -> `DateColumnFilterPopover`'s stored `(value,
 * operator)`, so it can restore what `criteria` (the URL) says on mount (S94/S95). `''`/`''` (no
 * filter) when both sides are blank. */
export const columnFilterFromCreatedDateRange = (createdFromMillis, createdToMillis) => {
  if (createdFromMillis === '' && createdToMillis === '') return { value: '', operator: '' }
  if (createdFromMillis !== '' && createdToMillis !== '') {
    const fromIso = localDateStringToIsoInstant(localMidnightMillisToDateString(createdFromMillis))
    const toIso = localDateStringToIsoInstant(exclusiveEndMillisToDateString(createdToMillis))
    return { operator: 'between', value: fromIso + RANGE_DELIMITER + toIso }
  }
  if (createdFromMillis !== '') {
    return { operator: 'gte', value: localDateStringToIsoInstant(localMidnightMillisToDateString(createdFromMillis)) }
  }
  return { operator: 'lte', value: localDateStringToIsoInstant(exclusiveEndMillisToDateString(createdToMillis)) }
}
